import { useMemo, useState } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, StatCard, Modal } from '../../components/ui'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useExpenses, useProducts, useSubscriptions } from '../../hooks'
import { useToast } from '../../context/ToastContext'
import { formatCurrency } from '../../lib/format'
import ProductOverviewGrid from '../../features/operations/ProductOverviewGrid'
import ProductForm from '../../features/operations/ProductForm'

function DashboardPage() {
  const { products, loading: productsLoading, createProduct } = useProducts()
  const { expenses, loading: expensesLoading } = useExpenses()
  const { monthlyTotal: recurringMonthlyExpenses, loading: subscriptionsLoading } = useSubscriptions()
  const [showAddModal, setShowAddModal] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const toast = useToast()

  const totals = useMemo(() => {
    const oneOffExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0)
    const totalExpenses = oneOffExpenses + recurringMonthlyExpenses
    const totalRevenue = products.reduce((sum) => sum + 0, 0)
    return {
      expenses: totalExpenses,
      oneOffExpenses,
      recurringMonthlyExpenses,
      revenue: totalRevenue,
      profit: totalRevenue - totalExpenses,
    }
  }, [expenses, products, recurringMonthlyExpenses])

  const activeProducts = products.filter(p => p.status === 'active').length
  const loading = productsLoading || expensesLoading || subscriptionsLoading

  const handleCreateProduct = async (formData) => {
    setFormLoading(true)
    const { error } = await createProduct(formData)
    setFormLoading(false)

    if (error) {
      toast.error('Failed to create product')
      return
    }

    toast.success('Product created')
    setShowAddModal(false)
  }

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <LoadingSpinner.Overlay message="Loading dashboard..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Dashboard"
      actions={
        <Button onClick={() => setShowAddModal(true)}>
          + Add Product
        </Button>
      }
    >
      <div className="grid grid-4 mb-8">
        <StatCard
          label="Total Revenue (placeholder)"
          value={formatCurrency(totals.revenue)}
          changeType="positive"
        />
        <StatCard
          label="Total Expenses (One-Off + Monthly Recurring)"
          value={formatCurrency(totals.expenses)}
          changeType="negative"
        />
        <StatCard
          label="Net Profit"
          value={formatCurrency(totals.profit)}
          changeType={totals.profit >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          label="Active Products"
          value={activeProducts}
        />
      </div>

      <div className="mb-4">
        <h2 className="text-xl font-bold mb-4">Business Overview</h2>
      </div>

      <ProductOverviewGrid
        products={products}
        onAdd={() => setShowAddModal(true)}
      />

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Product"
      >
        <ProductForm
          onSubmit={handleCreateProduct}
          onCancel={() => setShowAddModal(false)}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default DashboardPage
